<template>
    <v-modal title="Refund" @close="$emit('close')" size="small" scroll>
        <template slot="contents">
            <div class="modal-content" v-if="$v.$error">
                <div class="grid-item grid-item-full">
                    <v-error-messages>
                        <li class="list-item" v-if="!$v.refundingAmount.isInRange">
                            Refund total amount must be greater than 0 and less than {{maxRefundableAmount | monetize(invoice.currency)}}
                        </li>
                        <li class="list-item" v-if="!$v.refundingAmount.isFloat">
                            Refund amount must be a number
                        </li>
                        <template v-for="validate in $v.refundableItems.$each.$iter">
                            <li class="list-item" v-if="!validate.value.isInRange">{{validate.$model.product.name}} refund amount must be greater than 0 and less than {{maxRefundForItem(validate.$model.price) | monetize(invoice.currency)}}</li>
                            <li class="list-item" v-if="!validate.value.isFloat">{{validate.$model.product.name}} refund amount amount must be a number</li>
                        </template>
                    </v-error-messages>
                </div>
            </div>
            <div class="modal-content" v-if="hasMultipleRefundableItems">
                <r-checkbox
                    label="Check / Uncheck all"
                    :value="true"
                    :fuzzy="checkAllFuzzy"
                    v-model="checkAll"
                    @input="toggleAll"
                />
            </div>
            <div class="modal-content">
                <div class="grid-content" v-for="(item, index) in refundableItems" :key="item.id">
                    <div class="grid-item">
                        <r-checkbox v-model="checkedItems" :value="item.id">
                            <template slot="label">
                                <div>
                                    <p>{{item.product.name | truncate({limit: 32, dots: true})}}</p>
                                    <p class="text-muted" v-if="item.product.description || item.description">
                                        {{item.product.description ? item.product.description: item.description | truncate({limit: 32, dots: true})}}
                                    </p>
                                </div>
                            </template>
                        </r-checkbox>
                    </div>
                    <div class="grid-item grid-item-quarter">
                        <r-input
                            :left-label="`${getCurrencySign(invoice.currency)}`"
                            :validate="$v.refundableItems.$each.$iter[index]"
                            v-model="item.value"/>
                    </div>
                </div>
            </div>
            <div class="modal-content">
                <p class="stack-xs">Refund Amount</p>
                <div class="tile tile-secondary stack-xs">
                    <div class="tile-content">
                        <div class="grid">
                            <div class="grid-item grid-item-full display-flex justify-content-space-between">
                                <strong>Total</strong>
                                <strong>{{refundingAmount | monetizeExplicit(invoice.currency)}}</strong>
                            </div>
                        </div>
                    </div>
                </div>
                <p class="text-muted">Refund can only be in the same currency as the original transaction.</p>
            </div>
        </template>
        <template slot="actions">
            <r-button
                type="primary"
                @click="refund">Refund</r-button>
        </template>
    </v-modal>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import {
        vErrorMessages,
    } from '@/components/form';
    import _ from '@/common/helpers';
    import shortid from 'shortid';

    export default {
        components: {
            vModal, vErrorMessages,
        },
        props: {
            invoice: {type: Object},
            maxRefundableAmount: {type: Number},
        },
        data() {
            return {
                checkAll: false,
                checkedItems: [],
            };
        },
        computed: {
            hasTaxes() {
                return this.invoice.taxes.length > 0;
            },
            refundableItems() {
                const items = _.deepClone(this.invoice.items);
                if (this.invoice.hasShipping) {
                    items.push({
                        id: shortid.generate(),
                        product: {
                            name: 'Shipping',
                        },
                        unitPrice: this.invoice.shippingAmount,
                    });
                }
                if (this.hasTaxes) {
                    this.invoice.taxes.forEach((tax) => {
                        items.push({
                            id: shortid.generate(),
                            product: {
                                name: 'Tax',
                                description: tax.description,
                            },
                            unitPrice: tax.amount,
                        });
                    });
                }

                items.forEach((item) => {
                    let value = item.unitPrice * (item.quantity || 1);
                    if (value > this.maxRefundableAmount) {
                        value = this.maxRefundableAmount;
                    }
                    this.$set(item, 'value', value);
                });
                return items;
            },
            hasMultipleRefundableItems() {
                return this.refundableItems.length > 1;
            },
            refundingAmount() {
                return this.refundableItems
                    .filter(item => this.checkedItems.includes(item.id))
                    .reduce((value, item) => value + (parseFloat(item.value) || 0), 0);
            },
            checkAllFuzzy() {
                this.checkAll = this.checkedItems.length !== 0;
                return this.checkedItems.length > 0 && this.checkedItems.length < this.refundableItems.length;
            },
        },
        validations() {
            return {
                refundingAmount: {
                    isInRange: (value) => {
                        const float = parseFloat(value);
                        return !(float < 0 || float > this.maxRefundableAmount);
                    },
                    isFloat: value => !isNaN(parseFloat(value)),
                },
                refundableItems: {
                    $each: {
                        value: {
                            isInRange: (value, item) => {
                                if (this.isCheckedItem(item.id)) {
                                    const float = parseFloat(value);
                                    return !(float < 0 || float > parseFloat(item.price));
                                }
                                return true;
                            },
                            isFloat: (value, item) => {
                                if (this.isCheckedItem(item.id)) {
                                    return !isNaN(parseFloat(value));
                                }
                                return true;
                            },
                        },
                    },
                },
            };
        },
        methods: {
            refund() {
                this.$v.$touch();
                if (!this.$v.$invalid) {
                    this.$emit('refund', this.refundingAmount);
                    this.$emit('close');
                }
            },
            toggleAll(value) {
                if (value) {
                    this.checkedItems = this.refundableItems.map(item => item.id);
                } else {
                    this.checkedItems = [];
                }
            },
            isCheckedItem(id) {
                return this.checkedItems.includes(id);
            },
            getCurrencySign(currency) {
                return _.currencySign(currency);
            },
            maxRefundForItem(amount) {
                return amount > this.maxRefundableAmount ? this.maxRefundableAmount : amount;
            },
        },
        beforeMount() {
            this.toggleAll(true);
        },
    };
</script>
